import React, { useEffect, useMemo, useState } from "react";
import {
  deleteAdminContactUsRequest,
  getAdminContactUsRequests,
} from "../../../api/contactUsAdmin";
import { useNotification } from "../../../context/NotificationContext";

const ContactUsManager = () => {
  const { notify } = useNotification();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadItems = async () => {
    setLoading(true);
    try {
      const data = await getAdminContactUsRequests();
      setItems(Array.isArray(data) ? data : []);
    } catch (error) {
      notify({
        type: "error",
        title: "Failed to load contact requests",
        message: error?.response?.data?.message || error?.message || "",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const sortedItems = useMemo(
    () =>
      [...items].sort(
        (a, b) => new Date(b?.createdAtUtc || 0).getTime() - new Date(a?.createdAtUtc || 0).getTime()
      ),
    [items]
  );

  const handleDelete = async (item) => {
    const ok = window.confirm(`Delete contact request #${item?.id}?`);
    if (!ok) return;
    try {
      await deleteAdminContactUsRequest({ id: item.id });
      notify({ type: "success", title: "Contact request deleted" });
      await loadItems();
    } catch (error) {
      notify({
        type: "error",
        title: "Delete failed",
        message: error?.response?.data?.message || error?.message || "",
      });
    }
  };

  return (
    <section className="space-y-5">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#011337]">Contact Requests Manager</h2>
            <p className="text-sm text-slate-500 mt-1">Review and delete submitted contact-us requests.</p>
          </div>
          <button
            type="button"
            onClick={loadItems}
            className="px-4 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm"
          >
            Reload
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm bg-white">
        <table className="min-w-full table-fixed">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">ID</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Name</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Email</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Phone</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Location</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Course</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Experience</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Created</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} className="px-3 py-4 text-sm text-slate-500">
                  Loading contact requests...
                </td>
              </tr>
            ) : sortedItems.length ? (
              sortedItems.map((item) => (
                <tr key={item.id} className="border-b border-slate-200 align-top">
                  <td className="px-3 py-3 text-sm text-slate-700">{item.id}</td>
                  <td className="px-3 py-3 text-sm text-slate-900 font-medium">{item.name || "-"}</td>
                  <td className="px-3 py-3 text-sm text-slate-700 break-all">{item.email || "-"}</td>
                  <td className="px-3 py-3 text-sm text-slate-700">{item.phoneNumber || "-"}</td>
                  <td className="px-3 py-3 text-sm text-slate-700">{item.location || "-"}</td>
                  <td className="px-3 py-3 text-sm text-slate-700">{item.courseTitle || "-"}</td>
                  <td className="px-3 py-3 text-sm text-slate-700">{item.yearsOfExperience || "-"}</td>
                  <td className="px-3 py-3 text-sm text-slate-700">
                    {item.createdAtUtc ? new Date(item.createdAtUtc).toLocaleString() : "-"}
                  </td>
                  <td className="px-3 py-3">
                    <button
                      type="button"
                      onClick={() => handleDelete(item)}
                      className="text-xs px-2 py-1.5 rounded border border-rose-200 text-rose-700 hover:bg-rose-50"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={9} className="px-3 py-4 text-sm text-slate-500">
                  No contact requests found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default ContactUsManager;
